import {
  PASTIRADIT_MASK,
  PASTIRADIT_MASK_MOBILE,
  type WordMask,
} from "../art/letterMasks";
import {
  isBoldGlyph,
  randomFillGlyph,
  randomGlyph,
  SCRAMBLE_POOL,
} from "../art/pastiradit";
import {
  ALPHA_MIN,
  ALPHA_MAX,
  SHADOW_ALPHA,
  FONT_STACK,
  SHADOW_DIR_X,
  SHADOW_DIR_Y,
  ink,
} from "./theme";

export type UnicodeArt = {
  opacity: number;
  reform: () => void;
  scramble: (intensity: number) => void;
  update: (dt: number, now: number) => void;
  draw: (
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    viewportW: number,
    viewportH?: number,
  ) => void;
};

type Cell = {
  row: number;
  col: number;
  subRow: number;
  subCol: number;
  current: string;
  ttl: number;
};

type LayoutMode = "single" | "stacked";

const MOBILE_MAX_W = 640;

const WAVE_AMP = 0.55;
const WAVE_PERIOD = 5.5;
const EXTRUDE_LAYERS = 4;

const FLICKER_MIN = 0.04;
const FLICKER_MAX = 0.14;
const FONT_MIN = 3;
const FONT_MAX = 13;
/** Micro-glyphs per mask cell. Lower = looser fill. */
const SUBDIV = 2;

function nextTtl(rng = Math.random): number {
  return FLICKER_MIN + rng() * (FLICKER_MAX - FLICKER_MIN);
}

function modeForWidth(viewportW: number): LayoutMode {
  return viewportW < MOBILE_MAX_W ? "stacked" : "single";
}

function maskForMode(mode: LayoutMode): WordMask {
  return mode === "stacked" ? PASTIRADIT_MASK_MOBILE : PASTIRADIT_MASK;
}

function cellsFromMask(mask: WordMask): Cell[] {
  const out: Cell[] = [];
  for (const m of mask.cells) {
    for (let subRow = 0; subRow < SUBDIV; subRow++) {
      for (let subCol = 0; subCol < SUBDIV; subCol++) {
        out.push({
          row: m.row,
          col: m.col,
          subRow,
          subCol,
          current: randomFillGlyph(),
          ttl: nextTtl(),
        });
      }
    }
  }
  return out;
}

function layout(
  cols: number,
  rows: number,
  viewportW: number,
  viewportH: number,
  stacked: boolean,
) {
  const maxW = viewportW * (stacked ? 0.92 : 0.95);
  const maxH = viewportH * (stacked ? 0.68 : 0.6);
  const cellByW = maxW / cols;
  const cellByH = maxH / rows;
  const cell = Math.max(5, Math.min(cellByW, cellByH));
  const cellW = cell * 0.95;
  const cellH = cell * 1.32;
  const microW = cellW / SUBDIV;
  const microH = cellH / SUBDIV;
  const fontSize = Math.max(FONT_MIN, Math.min(FONT_MAX, Math.min(microW, microH) * 0.92));
  return {
    cellW,
    cellH,
    fontSize,
    totalW: cols * cellW,
    totalH: rows * cellH,
  };
}

export function createUnicodeArt(): UnicodeArt {
  let mode: LayoutMode = "single";
  let mask = maskForMode(mode);
  let rows = mask.rows;
  let cols = mask.cols;
  let cells = cellsFromMask(mask);

  let time = 0;
  let opacity = 1;
  let scrambleMode = 0;

  const applyMask = (next: WordMask) => {
    mask = next;
    rows = next.rows;
    cols = next.cols;
    cells = cellsFromMask(next);
  };

  const ensureMode = (viewportW: number) => {
    const next = modeForWidth(viewportW);
    if (next === mode) return;
    mode = next;
    applyMask(maskForMode(mode));
  };

  const reform = () => {
    scrambleMode = 0;
    for (const cell of cells) {
      cell.current = randomFillGlyph();
      cell.ttl = nextTtl() * 0.5;
    }
  };

  const scramble = (intensity: number) => {
    scrambleMode = Math.max(0, Math.min(1, intensity));
  };

  const update = (dt: number, _now: number) => {
    time += dt;

    const speed = 1 + scrambleMode * 4;
    const useScramblePool = scrambleMode > 0.2;

    for (const cell of cells) {
      cell.ttl -= dt * speed;
      if (cell.ttl <= 0) {
        cell.current = useScramblePool
          ? randomGlyph(SCRAMBLE_POOL)
          : randomFillGlyph();
        cell.ttl = nextTtl();
      }
      if (scrambleMode > 0.05 && Math.random() < scrambleMode * 0.35) {
        cell.current = randomGlyph(SCRAMBLE_POOL);
      }
    }
  };

  const draw: UnicodeArt["draw"] = (ctx, cx, cy, viewportW, viewportH) => {
    if (opacity <= 0.001) return;

    ensureMode(viewportW);

    const vh =
      viewportH ??
      (typeof window !== "undefined" ? window.innerHeight : 800);
    const { cellW, cellH, fontSize, totalW, totalH } = layout(
      cols,
      rows,
      viewportW,
      vh,
      mode === "stacked",
    );
    const originX = cx - totalW / 2;
    const originY = cy - totalH / 2;

    const breath = 0.5 + 0.5 * Math.sin(time * 0.55);
    const alpha =
      (ALPHA_MIN + (ALPHA_MAX - ALPHA_MIN) * breath) *
      opacity *
      (1 - scrambleMode * 0.25);

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const waveOn = true;
    const omega = (Math.PI * 2) / WAVE_PERIOD;
    const mediumFont = `500 ${fontSize}px ${FONT_STACK}`;
    const boldFont = `700 ${fontSize}px ${FONT_STACK}`;

    const waveAt = (col: number, row: number): number =>
      waveOn
        ? Math.sin(time * omega + col * 0.22 + row * 0.35) * WAVE_AMP
        : 0;

    const step = Math.max(1.8, fontSize * 0.22);
    const stepX = SHADOW_DIR_X * step;
    const stepY = SHADOW_DIR_Y * step;

    for (let layer = EXTRUDE_LAYERS; layer >= 1; layer--) {
      const layerA =
        SHADOW_ALPHA *
        opacity *
        ((EXTRUDE_LAYERS - layer + 1) / (EXTRUDE_LAYERS + 1));
      ctx.fillStyle = ink(layerA);
      for (const cell of cells) {
        const x =
          originX +
          (cell.col + (cell.subCol + 0.5) / SUBDIV) * cellW +
          stepX * layer;
        const y =
          originY +
          (cell.row + (cell.subRow + 0.5) / SUBDIV) * cellH +
          stepY * layer +
          waveAt(cell.col, cell.row);
        ctx.font = isBoldGlyph(cell.current) ? boldFont : mediumFont;
        ctx.fillText(cell.current, x, y);
      }
    }

    for (const cell of cells) {
      const x =
        originX + (cell.col + (cell.subCol + 0.5) / SUBDIV) * cellW;
      const y =
        originY +
        (cell.row + (cell.subRow + 0.5) / SUBDIV) * cellH +
        waveAt(cell.col, cell.row);
      ctx.font = isBoldGlyph(cell.current) ? boldFont : mediumFont;
      ctx.fillStyle = ink(alpha);
      ctx.fillText(cell.current, x, y);
    }

    ctx.restore();
  };

  return {
    get opacity() {
      return opacity;
    },
    set opacity(v: number) {
      opacity = Math.max(0, Math.min(1, v));
    },
    reform,
    scramble,
    update,
    draw,
  };
}
